import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, X, Clock, ArrowLeft, FileText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDeliveryNotes = () => {
  const { isStaff } = useAuth();
  const navigate = useNavigate();
  
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    if (!isStaff) {
      navigate('/connexion');
      return;
    }
    fetchNotes();
  }, [isStaff, navigate, filter]);

  const fetchNotes = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API_URL}/api/delivery-notes${params}`, {
        
      });
      setNotes(response.data);
    } catch (err) {
      // Error shown via loading=false + null data check in UI
    } finally {
      setLoading(false);
    }
  };

  const confirmPayment = async (noteId) => {
    if (!window.confirm('Confirmer le paiement et déduire le stock ?')) return;
    
    try {
      await axios.put(`${API_URL}/api/delivery-notes/${noteId}/confirm`, {}, {
        
      });
      fetchNotes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
  };

  const cancelNote = async (noteId) => {
    if (!window.confirm('Annuler ce bon de livraison ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/delivery-notes/${noteId}`, {
        
      });
      fetchNotes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'text-yellow-500 bg-yellow-500/10 border-yellow-500/30',
      completed: 'text-green-500 bg-green-500/10 border-green-500/30',
      cancelled: 'text-red-500 bg-red-500/10 border-red-500/30'
    };
    const labels = {
      pending: 'En attente',
      completed: 'Confirmé',
      cancelled: 'Annulé'
    };
    return (
      <span className={`px-3 py-1 text-xs uppercase tracking-wider border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/admin" className="text-zinc-500 hover:text-white text-sm mb-2 inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Dashboard
            </Link>
            <h1 
              className="text-3xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Bons de Livraison
            </h1>
          </div>
          <Link
            to="/admin/preparation"
            className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-6 py-3 hover:bg-[color:var(--color-primary-hover)] transition-colors"
            data-testid="new-preparation"
          >
            <FileText className="w-5 h-5" />
            Nouvelle préparation
          </Link>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {['pending', 'completed', 'cancelled', 'all'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                filter === status
                  ? 'bg-[color:var(--color-primary)] text-black'
                  : 'border border-white/10 text-zinc-400 hover:border-white/20'
              }`}
            >
              {status === 'all' ? 'Tous' : status === 'pending' ? 'En attente' : status === 'completed' ? 'Confirmés' : 'Annulés'}
            </button>
          ))}
        </div>

        {/* Notes List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={`skeleton-${i}`} className="bg-[color:var(--color-surface)] border border-white/10 h-32 animate-pulse" />
            ))}
          </div>
        ) : notes.length > 0 ? (
          <div className="space-y-4">
            {notes.map(note => (
              <div 
                key={note.id}
                className="bg-[color:var(--color-surface)] border border-white/10 p-6"
                data-testid={`note-${note.id}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-white">#{note.id.slice(-8).toUpperCase()}</h3>
                      {getStatusBadge(note.status)}
                    </div>
                    <p className="text-zinc-500 text-sm mt-1 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {new Date(note.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <span 
                    className="text-2xl text-[color:var(--color-primary)] font-light"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {note.total.toFixed(2)}€
                  </span>
                </div>

                {/* Customer */}
                <div className="mb-4 pb-4 border-b border-white/5">
                  <p className="text-white">{note.customer_name}</p>
                  {note.customer_phone && (
                    <p className="text-zinc-500 text-sm">{note.customer_phone}</p>
                  )}
                </div>

                {/* Items */}
                <div className="mb-4">
                  <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Produits</p>
                  <div className="space-y-1">
                    {note.items.slice(0, 3).map((item, idx) => (
                      <div key={`${item.product_name}-${idx}`} className="flex justify-between text-sm">
                        <span className="text-zinc-400">
                          {item.product_name} x{item.quantity}
                          <span className="text-zinc-600 ml-1">({item.tier})</span>
                        </span>
                        <span className="text-white">{item.total.toFixed(2)}€</span>
                      </div>
                    ))}
                    {note.items.length > 3 && (
                      <p className="text-zinc-600 text-xs">+{note.items.length - 3} autres produits</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {note.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => confirmPayment(note.id)}
                      className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-2 hover:bg-green-700 transition-colors"
                      data-testid={`confirm-${note.id}`}
                    >
                      <Check className="w-4 h-4" />
                      Confirmer paiement
                    </button>
                    <button
                      onClick={() => cancelNote(note.id)}
                      className="flex items-center justify-center gap-2 border border-red-500/50 text-red-500 px-4 py-2 hover:bg-red-500/10 transition-colors"
                      data-testid={`cancel-${note.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                
                {note.status === 'completed' && note.confirmed_at && (
                  <p className="text-green-500 text-xs">
                    Confirmé le {new Date(note.confirmed_at).toLocaleString('fr-FR')}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-[color:var(--color-surface)] border border-white/10">
            <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500">Aucun bon de livraison</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDeliveryNotes;
